/**
 * 작업완료보고서 만들기 — 한 일을 적으면 고객에게 낼 수 있는 보고서가 바로 나온다.
 *
 * 유입 엔진의 네 번째 도구이고 **경쟁이 가장 얕은 자리**다 (docs/goals §4-b · reportRules 주석).
 * 견적서 계열과 화면 껍데기는 공유하지만(`qb-*` 스타일) 문서 구조가 달라 컴포넌트는 따로다.
 *
 * 신뢰 조건은 같다: 로그인 없음 · 서버 전송 없음 · 인쇄는 `window.print()`.
 */
import { useEffect, useMemo, useState } from 'react'
import { Badge, Button, Card, Container, Text } from '@cottonray/ui-web'
import { progressText, reportStatus, statusLabel, usableWork, type ReportInput, type WorkItem } from './reportRules'

const STORAGE_KEY = 'completion-report-v1'
const EMPTY: WorkItem = { description: '', done: true }

const DEFAULTS: ReportInput = {
  contractor: '',
  client: '',
  site: '',
  workedOn: new Date().toISOString().slice(0, 10),
  items: [{ ...EMPTY }, { ...EMPTY }, { ...EMPTY }],
  note: '',
}

function load(): ReportInput {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === null) return DEFAULTS
    const saved = JSON.parse(raw) as Partial<ReportInput>
    // 작업일은 저장값을 쓰지 않는다 — 지난 보고서를 열어 그대로 인쇄하면 날짜가 틀린다
    return { ...DEFAULTS, ...saved, workedOn: DEFAULTS.workedOn }
  } catch {
    return DEFAULTS
  }
}

export function CompletionReport() {
  const [state, setState] = useState<ReportInput>(load)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
    } catch {
      // 저장 실패가 도구를 막지는 않는다
    }
  }, [state])

  const rows = useMemo(() => usableWork(state.items), [state.items])
  const status = useMemo(() => reportStatus(state.items), [state.items])
  const progress = useMemo(() => progressText(state.items), [state.items])

  function setItem(index: number, patch: Partial<WorkItem>): void {
    setState((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }))
  }

  return (
    <>
      <section className="qb-hero">
        <Container>
          <Badge tone="accent">무료 · 가입 없음</Badge>
          <Text variant="display" className="qb-hero__title">
            작업완료보고서 만들기
          </Text>
          <Text tone="muted" className="qb-hero__lead">
            한 일을 적으면 고객에게 낼 수 있는 완료보고서가 바로 나옵니다.
            <strong> 입력한 내용은 이 브라우저에만 남고 어디로도 전송되지 않습니다.</strong>
          </Text>
        </Container>
      </section>

      <section className="qb-body">
        <Container>
          <Card className="qb-form qb-noprint">
            <div className="qb-grid">
              <label>
                <Text variant="label">시공자 (내 상호)</Text>
                <input
                  value={state.contractor}
                  onChange={(e) => setState({ ...state, contractor: e.target.value })}
                  placeholder="예: 코튼레이 청소"
                />
              </label>
              <label>
                <Text variant="label">고객 · 현장명</Text>
                <input
                  value={state.client}
                  onChange={(e) => setState({ ...state, client: e.target.value })}
                  placeholder="예: ○○빌딩 관리사무소"
                />
              </label>
              <label>
                <Text variant="label">현장 위치 (선택)</Text>
                <input
                  value={state.site}
                  onChange={(e) => setState({ ...state, site: e.target.value })}
                  placeholder="예: 3층 공용 화장실"
                />
              </label>
              <label>
                <Text variant="label">작업일</Text>
                <input
                  type="date"
                  value={state.workedOn}
                  onChange={(e) => setState({ ...state, workedOn: e.target.value })}
                />
              </label>
            </div>

            <Text variant="label" className="qb-items__label">
              한 일 (체크하면 완료)
            </Text>
            <div className="qb-items">
              {state.items.map((item, index) => (
                <div className="cr-item" key={index}>
                  <input
                    type="checkbox"
                    checked={item.done}
                    onChange={(e) => setItem(index, { done: e.target.checked })}
                    aria-label="완료 여부"
                  />
                  <input
                    value={item.description}
                    onChange={(e) => setItem(index, { description: e.target.value })}
                    placeholder="예: 배수구 이물질 제거 및 살균"
                  />
                </div>
              ))}
            </div>

            <div className="qb-actions">
              <Button
                variant="secondary"
                onClick={() => setState({ ...state, items: [...state.items, { ...EMPTY }] })}
              >
                줄 추가
              </Button>
              <Button onClick={() => window.print()} disabled={rows.length === 0}>
                인쇄 · PDF 로 저장
              </Button>
            </div>

            <label className="qb-note">
              <Text variant="label">특이사항 (선택)</Text>
              <textarea
                value={state.note}
                onChange={(e) => setState({ ...state, note: e.target.value })}
                placeholder="예: 배관 노후로 재시공 권장, 사진 별도 전달"
                rows={2}
              />
            </label>
          </Card>

          <Card className="qb-doc">
            <div className="qb-doc__head">
              <Text variant="title">작업완료보고서</Text>
              <div className="qb-doc__meta">
                <span>작업일 {state.workedOn}</span>
                {progress === '' ? null : <span>완료 {progress}</span>}
              </div>
            </div>

            <div className="qb-doc__parties">
              <div>
                <Text variant="label">시공자</Text>
                <Text>{state.contractor.trim() === '' ? '—' : state.contractor}</Text>
              </div>
              <div>
                <Text variant="label">고객 · 현장</Text>
                <Text>
                  {state.client.trim() === '' ? '—' : state.client}
                  {state.site.trim() === '' ? '' : ` (${state.site})`}
                </Text>
              </div>
            </div>

            {rows.length === 0 ? (
              <Text tone="muted" className="qb-doc__empty">
                한 일을 적으면 여기에 보고서가 그려집니다.
              </Text>
            ) : (
              <>
                <table className="qb-table">
                  <thead>
                    <tr>
                      <th>작업 내용</th>
                      <th className="qb-right">상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((item, index) => (
                      <tr key={index}>
                        <td>{item.description}</td>
                        <td className="qb-right">{item.done ? '완료' : '미완'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Text className="cr-status">{statusLabel(status)}</Text>
              </>
            )}

            {state.note.trim() === '' ? null : (
              <div className="qb-doc__note">
                <Text variant="label">특이사항</Text>
                <Text>{state.note}</Text>
              </div>
            )}

            <div className="cr-sign">
              <Text variant="label">고객 확인</Text>
              <div className="cr-sign__line" />
            </div>
          </Card>

          <Card className="qb-upsell qb-noprint">
            <Text variant="label">사진으로 자동 작성</Text>
            <Text tone="muted">
              작업 전·후 사진을 올리면 보고서에 자동으로 붙이고 견적서까지 함께 만들어 주는 것을 준비하고
              있습니다. 손으로 넣는 지금 이 도구는 계속 무료입니다.
            </Text>
            <a className="qb-upsell__link" href="/field-docs">
              어떤 도구인지 보기 →
            </a>
          </Card>
        </Container>
      </section>
    </>
  )
}
